import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import AdminService from '../services/AdminService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createSparkle, createGlow, createSuccessBurst } from '../utils/vfx';
// React Icons imports
import {
  FaChartBar,
  FaUsers,
  FaUserShield,
  FaBan,
  FaTrash,
  FaSearch,
  FaCrown,
  FaUserTimes,
  FaUserCheck,
  FaArrowLeft,
  FaEye,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaGraduationCap,
  FaClipboardCheck,
  FaComments,
  FaExchangeAlt,
  FaFire,
  FaTrophy,
  FaBook,
  FaPlus,
  FaUserPlus,
  FaBars,
  FaRobot
} from 'react-icons/fa';
import AdminChallengesView from './AdminChallengesView';
import AdminLessonsView from './AdminLessonsView';
import AdminQuizzesView from './AdminQuizzesView';
import AdminAIStudio from './AdminAIStudio';
import LeagueService from '../services/LeagueService';
import './AdminPanel.css';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { state } = useUser();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [showAdminLevelModal, setShowAdminLevelModal] = useState(false);
  const [selectedUserForLevel, setSelectedUserForLevel] = useState(null);
  const [selectedAdminLevel, setSelectedAdminLevel] = useState('1');
  const [leagues, setLeagues] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [newLeague, setNewLeague] = useState({ name: '', description: '' });
  const [showAssignTeacher, setShowAssignTeacher] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    // Check if user is admin
    const isAdmin = state.user?.isAdmin || 
                    state.user?.adminLevel === '1' || 
                    state.user?.adminLevel === '2' || 
                    state.user?.adminLevel === '3' || 
                    state.user?.adminLevel === 'master_admin';
    if (!state.user || !isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }

    soundEffects.playPageTransition();
    loadData();
  }, [state.user, navigate, activeTab, currentPage, searchTerm]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.admin-tabs-container')) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMobileMenu]);

  // Close mobile menu when window is resized to larger screen
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && showMobileMenu) {
        setShowMobileMenu(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showMobileMenu]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'analytics') {
        const data = await AdminService.getAnalytics();
        setAnalytics(data.analytics);
      } else if (activeTab === 'users') {
        const data = await AdminService.getUsers(currentPage, 20, searchTerm);
        setUsers(data.users);
        setTotalPages(data.pagination.totalPages);
      } else if (activeTab === 'leagues') {
        const leaguesData = await LeagueService.getLeagues();
        setLeagues(leaguesData.leagues || []);
        const teachersData = await LeagueService.getTeachers();
        setTeachers(teachersData.teachers || []);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (action, userId, additionalData = {}) => {
    try {
      setActionLoading({ ...actionLoading, [userId]: true });
      let message = '';

      switch (action) {
        case 'ban':
          await AdminService.banUser(userId, additionalData.reason);
          message = 'User banned successfully';
          break;
        case 'unban':
          await AdminService.unbanUser(userId);
          message = 'User unbanned successfully';
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            await AdminService.deleteUser(userId);
            message = 'User deleted successfully';
          } else {
            return;
          }
          break;
        case 'makeAdmin':
          // This will be handled by the modal
          setSelectedUserForLevel({ id: userId, action: 'make' });
          setShowAdminLevelModal(true);
          setActionLoading({ ...actionLoading, [userId]: false });
          return;
        case 'editAdminLevel':
          // This will be handled by the modal
          setSelectedUserForLevel({ id: userId, action: 'edit', currentLevel: additionalData.currentLevel });
          setSelectedAdminLevel(additionalData.currentLevel || '1');
          setShowAdminLevelModal(true);
          setActionLoading({ ...actionLoading, [userId]: false });
          return;
        case 'updateAdminLevel':
          await AdminService.updateAdminLevel(userId, additionalData.adminLevel);
          message = `Admin level updated to ${additionalData.adminLevel === 'master_admin' ? 'Master Admin' : `Level ${additionalData.adminLevel}`} successfully`;
          break;
        case 'removeAdmin':
          await AdminService.removeAdmin(userId);
          message = 'Admin status removed successfully';
          break;
        case 'setTier':
          await AdminService.updateUserTier(userId, additionalData.tier);
          message = `Membership set to ${additionalData.tier === 'pre_alpha' ? 'Pre-Alpha' : additionalData.tier === 'pro' ? 'Pro' : 'Free'}`;
          break;
        case 'deleteGuests': {
          const { count } = await AdminService.getGuestCount();
          if (count === 0) {
            toast('No guest accounts to delete');
            setActionLoading({ ...actionLoading, guests: false });
            return;
          }
          if (!window.confirm(`Delete ALL ${count} guest account(s) and their data? This cannot be undone.`)) {
            setActionLoading({ ...actionLoading, guests: false });
            return;
          }
          const result = await AdminService.deleteAllGuests();
          message = `Deleted ${result.deletedCount} guest account(s)`;
          break;
        }
        case 'makeTeacher':
          await LeagueService.makeTeacher(userId);
          message = 'User promoted to teacher successfully';
          break;
        case 'removeTeacher':
          await LeagueService.removeTeacherStatus(userId);
          message = 'Teacher status removed successfully';
          break;
        default:
          return;
      }

      toast.success(message);
      loadData();
      if (showUserModal) {
        setShowUserModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to perform action');
    } finally {
      setActionLoading({ ...actionLoading, [userId]: false });
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const data = await AdminService.getUser(userId);
      setSelectedUser(data.user);
      setShowUserModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load user details');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper functions for admin levels
  const getAdminLevelNumber = (adminLevel) => {
    if (!adminLevel) return 0;
    if (adminLevel === 'master_admin') return 999;
    const level = parseInt(adminLevel);
    return isNaN(level) ? 0 : level;
  };

  const hasAdminLevel = (minLevel) => {
    const userLevel = getAdminLevelNumber(state.user?.adminLevel);
    const requiredLevel = getAdminLevelNumber(minLevel);
    return userLevel >= requiredLevel;
  };

  // Check if current admin can act on target user
  const canActOnUser = (targetUser) => {
    // If target is not an admin, anyone can act on them
    const targetIsAdmin = targetUser.isAdmin || targetUser.adminLevel === '1' || targetUser.adminLevel === '2' || targetUser.adminLevel === '3' || targetUser.adminLevel === 'master_admin';
    if (!targetIsAdmin) {
      return true;
    }

    const currentLevel = getAdminLevelNumber(state.user?.adminLevel);
    const targetLevel = getAdminLevelNumber(targetUser.adminLevel);

    // Master admin can act on anyone
    if (currentLevel === 999) {
      return true;
    }

    // Current admin must have higher level than target admin
    return currentLevel > targetLevel;
  };

  const handleTabClick = (tabName, e) => {
    soundEffects.playClick();
    createRipple(e);
    setActiveTab(tabName);
    setShowMobileMenu(false); // Close mobile menu when tab is selected
  };

  const isAdmin = state.user?.isAdmin || 
                  state.user?.adminLevel === '1' || 
                  state.user?.adminLevel === '2' || 
                  state.user?.adminLevel === '3' || 
                  state.user?.adminLevel === 'master_admin';
  const isMasterAdmin = state.user?.adminLevel === 'master_admin';
  const adminLevel = state.user?.adminLevel || (state.user?.isAdmin ? '3' : null);
  const effectiveAdminLevel = state.user?.adminLevel || (state.user?.isAdmin ? '3' : null);
  const canDeleteClassrooms =
    getAdminLevelNumber(effectiveAdminLevel) >= 3;
  const isTopAdmin = state.user?.email === 'depedroshawn@gmail.com';
  
  if (!state.user || !isAdmin) {
    return null;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <button 
          className="back-button" 
          onClick={(e) => {
            soundEffects.playClick();
            createRipple(e);
            navigate('/dashboard');
          }}
          onMouseEnter={() => soundEffects.playHover()}
        >
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1>Admin Panel</h1>
        <div className="admin-user-info">
          <FaUserShield /> {state.user.name || state.user.email}
          {adminLevel === 'master_admin' && <span className="master-admin-badge">Master Admin</span>}
          {adminLevel === '3' && <span className="admin-level-badge level-3">Level 3</span>}
          {adminLevel === '2' && <span className="admin-level-badge level-2">Level 2</span>}
          {adminLevel === '1' && <span className="admin-level-badge level-1">Level 1</span>}
        </div>
      </div>

      <div className="admin-tabs-container">
        <button 
          className="mobile-menu-toggle"
          onClick={(e) => {
            soundEffects.playClick();
            createRipple(e);
            setShowMobileMenu(!showMobileMenu);
          }}
          onMouseEnter={() => soundEffects.playHover()}
          aria-label="Toggle menu"
        >
          <FaBars />
          <span>Menu</span>
        </button>
        
        <div className={`admin-tabs ${showMobileMenu ? 'mobile-menu-open' : ''}`}>
          <button
            className={activeTab === 'analytics' ? 'active' : ''}
            onClick={(e) => handleTabClick('analytics', e)}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaChartBar /> <span>Analytics</span>
          </button>
          <button
            className={activeTab === 'users' ? 'active' : ''}
            onClick={(e) => handleTabClick('users', e)}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaUsers /> <span>Users</span>
          </button>
          <button
            className={activeTab === 'communication' ? 'active' : ''}
            onClick={(e) => handleTabClick('communication', e)}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaComments /> <span>Communication</span>
          </button>
          {hasAdminLevel('2') && (
            <button
              className={activeTab === 'challenges' ? 'active' : ''}
              onClick={(e) => handleTabClick('challenges', e)}
              onMouseEnter={() => soundEffects.playHover()}
            >
              <FaTrophy /> <span>Challenges</span>
            </button>
          )}
          {hasAdminLevel('2') && (
            <button
              className={activeTab === 'lessons' ? 'active' : ''}
              onClick={(e) => handleTabClick('lessons', e)}
              onMouseEnter={() => soundEffects.playHover()}
            >
              <FaBook /> <span>Lessons</span>
            </button>
          )}
          {hasAdminLevel('2') && (
            <button
              className={activeTab === 'quizzes' ? 'active' : ''}
              onClick={(e) => handleTabClick('quizzes', e)}
              onMouseEnter={() => soundEffects.playHover()}
            >
              <FaClipboardCheck /> <span>Quizzes</span>
            </button>
          )}
          {hasAdminLevel('2') && (
            <button
              className={activeTab === 'ai' ? 'active' : ''}
              onClick={(e) => handleTabClick('ai', e)}
              onMouseEnter={() => soundEffects.playHover()}
            >
              <FaRobot /> <span>AI Studio</span>
            </button>
          )}
          <button
            className={activeTab === 'leagues' ? 'active' : ''}
            onClick={(e) => handleTabClick('leagues', e)}
            onMouseEnter={() => soundEffects.playHover()}
          >
            <FaUsers /> <span>Leagues</span>
          </button>
        </div>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : activeTab === 'analytics' ? (
          <AnalyticsView analytics={analytics} />
        ) : activeTab === 'communication' ? (
          <AdminCommunicationView adminLevel={adminLevel} />
        ) : activeTab === 'challenges' ? (
          <AdminChallengesView adminLevel={adminLevel} hasAdminLevel={hasAdminLevel} />
        ) : activeTab === 'lessons' ? (
          <AdminLessonsView adminLevel={adminLevel} hasAdminLevel={hasAdminLevel} />
        ) : activeTab === 'quizzes' ? (
          <AdminQuizzesView adminLevel={adminLevel} hasAdminLevel={hasAdminLevel} />
        ) : activeTab === 'ai' ? (
          <AdminAIStudio />
        ) : activeTab === 'leagues' ? (
          <LeaguesView
            leagues={leagues}
            teachers={teachers}
            selectedLeague={selectedLeague}
            setSelectedLeague={setSelectedLeague}
            showCreateLeague={showCreateLeague}
            setShowCreateLeague={setShowCreateLeague}
            newLeague={newLeague}
            setNewLeague={setNewLeague}
            showAssignTeacher={showAssignTeacher}
            setShowAssignTeacher={setShowAssignTeacher}
            onRefresh={loadData}
            canDeleteClassrooms={canDeleteClassrooms}
          />
        ) : (
          <UsersView
            users={users}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            onViewUser={viewUserDetails}
            onUserAction={handleUserAction}
            actionLoading={actionLoading}
            isMasterAdmin={isMasterAdmin}
            isTopAdmin={isTopAdmin}
            adminLevel={adminLevel}
            hasAdminLevel={hasAdminLevel}
            canActOnUser={canActOnUser}
          />
        )}
      </div>

      {showUserModal && selectedUser && (
        <UserModal
          user={selectedUser}
          currentUserIsMasterAdmin={isMasterAdmin}
          isTopAdmin={isTopAdmin}
          adminLevel={adminLevel}
          hasAdminLevel={hasAdminLevel}
          canActOnUser={canActOnUser}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onAction={handleUserAction}
          actionLoading={actionLoading}
        />
      )}

      {showAdminLevelModal && selectedUserForLevel && (
        <AdminLevelModal
          userId={selectedUserForLevel.id}
          action={selectedUserForLevel.action}
          currentLevel={selectedUserForLevel.currentLevel}
          selectedLevel={selectedAdminLevel}
          onLevelChange={setSelectedAdminLevel}
          onConfirm={async () => {
            try {
              if (selectedUserForLevel.action === 'make') {
                await AdminService.makeAdmin(selectedUserForLevel.id, selectedAdminLevel);
                toast.success(`User promoted to ${selectedAdminLevel === 'master_admin' ? 'Master Admin' : `Level ${selectedAdminLevel}`} successfully`);
              } else {
                await AdminService.updateAdminLevel(selectedUserForLevel.id, selectedAdminLevel);
                toast.success(`Admin level updated to ${selectedAdminLevel === 'master_admin' ? 'Master Admin' : `Level ${selectedAdminLevel}`} successfully`);
              }
              setShowAdminLevelModal(false);
              setSelectedUserForLevel(null);
              loadData();
            } catch (error) {
              toast.error(error.message || 'Failed to update admin level');
            }
          }}
          onClose={() => {
            setShowAdminLevelModal(false);
            setSelectedUserForLevel(null);
          }}
        />
      )}
    </div>
  );
};

const AnalyticsView = ({ analytics }) => {
  if (!analytics) return <div>No analytics data available</div>;

  const { overview, engagement, topUsers, recentActivity } = analytics;

  return (
    <div className="analytics-view">
      <div className="analytics-grid">
        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <FaUsers className="stat-icon" />
          <div className="stat-value">{overview.totalUsers}</div>
          <div className="stat-label">Total Users</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <FaFire className="stat-icon" />
          <div className="stat-value">{overview.activeUsers}</div>
          <div className="stat-label">Active Users (30d)</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <FaUserCheck className="stat-icon" />
          <div className="stat-value">{overview.newUsers}</div>
          <div className="stat-label">New Users (7d)</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <FaBan className="stat-icon" />
          <div className="stat-value">{overview.bannedUsers}</div>
          <div className="stat-label">Banned Users</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <FaGraduationCap className="stat-icon" />
          <div className="stat-value">{engagement.lessonsCompleted}</div>
          <div className="stat-label">Lessons Completed</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <FaClipboardCheck className="stat-icon" />
          <div className="stat-value">{engagement.quizAttempts}</div>
          <div className="stat-label">Quiz Attempts</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <FaComments className="stat-icon" />
          <div className="stat-value">{engagement.chatMessages}</div>
          <div className="stat-label">Chat Messages</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <FaExchangeAlt className="stat-icon" />
          <div className="stat-value">{engagement.transactions}</div>
          <div className="stat-label">Transactions</div>
        </motion.div>
      </div>

      <div className="analytics-sections">
        <div className="analytics-section">
          <h2>Top Users by XP</h2>
          <div className="top-users-list">
            {topUsers && topUsers.length > 0 ? (
              topUsers.map((user, index) => (
                <div key={user.id} className="top-user-item">
                  <span className="rank">#{index + 1}</span>
                  <span className="name">{user.name || user.email}</span>
                  <span className="xp">{user.xp || 0} XP</span>
                  <span className="level">Level {user.level || 1}</span>
                </div>
              ))
            ) : (
              <div>No user data available</div>
            )}
          </div>
        </div>

        <div className="analytics-section">
          <h2>Recent Activity (24h)</h2>
          <div className="recent-activity-list">
            {recentActivity && recentActivity.length > 0 ? (
              recentActivity.slice(0, 20).map((activity, index) => {
                const isTransaction = activity.type === 'stock_bought' || activity.type === 'stock_sold';
                const typeLabel = activity.type === 'stock_bought' ? 'BUY' : 
                                 activity.type === 'stock_sold' ? 'SELL' : 
                                 activity.type.replace('_', ' ').toUpperCase();
                
                return (
                  <div key={index} className="activity-item">
                    <span className={`activity-type ${isTransaction ? (activity.type === 'stock_bought' ? 'activity-type-buy' : 'activity-type-sell') : ''}`}>
                      {typeLabel}
                    </span>
                    <span className="activity-user">{activity.name || activity.email}</span>
                    {isTransaction && activity.symbol && (
                      <span className="activity-details">
                        {activity.symbol} - {parseFloat(activity.shares).toFixed(2)} shares @ ${parseFloat(activity.price).toFixed(2)} (${parseFloat(activity.total).toFixed(2)})
                      </span>
                    )}
                    <span className="activity-time">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                );
              })
            ) : (
              <div>No recent activity</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const UsersView = ({
  users,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  totalPages,
  onViewUser,
  onUserAction,
  actionLoading,
  isMasterAdmin,
  isTopAdmin,
  adminLevel,
  hasAdminLevel,
  canActOnUser
}) => {
  return (
    <div className="users-view">
      <div className="users-header">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        {hasAdminLevel('3') && (
          <button
            className="btn-delete-guests"
            onClick={() => onUserAction('deleteGuests', 'guests')}
            disabled={actionLoading.guests}
            title="Permanently delete all guest accounts (guest_...@moneysmart.app) and their data"
          >
            <FaUserTimes /> {actionLoading.guests ? 'Deleting…' : 'Delete All Guests'}
          </button>
        )}
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Level</th>
              <th>XP</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users && users.length > 0 ? (
              users.map((user) => {
                const targetIsAdmin = user.isAdmin || user.adminLevel === 'admin' || user.adminLevel === 'master_admin';
                const targetIsMasterAdmin = user.adminLevel === 'master_admin';
                const canManageAdmins = isMasterAdmin;
                
                return (
                <tr key={user.id}>
                  <td>{user.name || 'N/A'}</td>
                  <td>{user.email}</td>
                  <td>{user.level || 1}</td>
                  <td>{user.xp || 0}</td>
                  <td>
                    <div className="status-badges">
                      {user.adminLevel === 'master_admin' && <span className="badge master-admin">Master Admin</span>}
                      {user.adminLevel === '3' && <span className="badge admin level-3">Level 3</span>}
                      {user.adminLevel === '2' && <span className="badge admin level-2">Level 2</span>}
                      {user.adminLevel === '1' && <span className="badge admin level-1">Level 1</span>}
                      {user.isAdmin && !user.adminLevel && <span className="badge admin">Admin</span>}
                      {user.isBanned && <span className="badge banned">Banned</span>}
                      {!user.isAdmin && !user.adminLevel && !user.isBanned && (
                        <span className="badge active">Active</span>
                      )}
                    </div>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => onViewUser(user.id)}
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      {(() => {
                        const targetIsAdmin = user.isAdmin || user.adminLevel === '1' || user.adminLevel === '2' || user.adminLevel === '3' || user.adminLevel === 'master_admin';
                        const targetIsMasterAdmin = user.adminLevel === 'master_admin';
                        const canManageAdmins = isMasterAdmin;
                        const canActOnThisUser = canActOnUser(user);
                        
                        return (
                          <>
                            {/* Ban/Unban - Level 2+ can ban, but only if they can act on the user */}
                            {hasAdminLevel('2') && canActOnThisUser && (
                              <>
                                {user.isBanned ? (
                                  <button
                                    className="btn-unban"
                                    onClick={() => onUserAction('unban', user.id)}
                                    disabled={actionLoading[user.id]}
                                    title="Unban User"
                                  >
                                    <FaUserCheck />
                                  </button>
                                ) : (
                                  <button
                                    className="btn-ban"
                                    onClick={() => onUserAction('ban', user.id)}
                                    disabled={actionLoading[user.id] || targetIsMasterAdmin}
                                    title={targetIsMasterAdmin ? "Cannot ban Master Admin" : "Ban User"}
                                  >
                                    <FaBan />
                                  </button>
                                )}
                              </>
                            )}
                            
                            {/* Delete - Level 3+ can delete, but only if they can act on the user */}
                            {/* Top admin (depedroshawn@gmail.com) can delete master admins */}
                            {hasAdminLevel('3') && canActOnThisUser && (!targetIsMasterAdmin || isTopAdmin) && (
                              <button
                                className="btn-delete"
                                onClick={() => onUserAction('delete', user.id)}
                                disabled={actionLoading[user.id]}
                                title="Delete User"
                              >
                                <FaTrash />
                              </button>
                            )}
                            
                            {/* Make/Edit/Remove Admin - Only master admin can manage admins */}
                            {canManageAdmins && (
                              !targetIsAdmin ? (
                                <button
                                  className="btn-admin"
                                  onClick={() => onUserAction('makeAdmin', user.id)}
                                  disabled={actionLoading[user.id]}
                                  title="Make Admin"
                                >
                                  <FaCrown />
                                </button>
                              ) : !targetIsMasterAdmin ? (
                                <>
                                  <button
                                    className="btn-edit-admin"
                                    onClick={() => onUserAction('editAdminLevel', user.id, { currentLevel: user.adminLevel })}
                                    disabled={actionLoading[user.id]}
                                    title="Edit Admin Level"
                                  >
                                    <FaUserShield />
                                  </button>
                                  <button
                                    className="btn-remove-admin"
                                    onClick={() => onUserAction('removeAdmin', user.id)}
                                    disabled={actionLoading[user.id]}
                                    title="Remove Admin"
                                  >
                                    <FaUserTimes />
                                  </button>
                                </>
                              ) : null
                            )}
                            
                            {/* Make/Remove Teacher - Admins can manage teachers */}
                            {hasAdminLevel('1') && (
                              !user.isTeacher ? (
                                <button
                                  className="btn-teacher"
                                  onClick={() => onUserAction('makeTeacher', user.id)}
                                  disabled={actionLoading[user.id]}
                                  title="Make Teacher"
                                >
                                  <FaGraduationCap />
                                </button>
                              ) : (
                                <button
                                  className="btn-remove-teacher"
                                  onClick={() => onUserAction('removeTeacher', user.id)}
                                  disabled={actionLoading[user.id]}
                                  title="Remove Teacher"
                                >
                                  <FaUserTimes />
                                </button>
                              )
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const UserModal = ({ user, currentUserIsMasterAdmin, isTopAdmin, adminLevel, hasAdminLevel, canActOnUser, onClose, onAction, actionLoading }) => {
  const [banReason, setBanReason] = useState('');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ note: '', type: 'general' });
  const [addMoneyAmount, setAddMoneyAmount] = useState('');
  const [addMoneyReason, setAddMoneyReason] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [adminTransactions, setAdminTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    if (user && hasAdminLevel('1')) {
      loadNotes();
    }
    if (user && hasAdminLevel('3')) {
      loadAdminTransactions();
    }
  }, [user]);

  const loadNotes = async () => {
    try {
      setLoadingNotes(true);
      const data = await AdminService.getUserNotes(user.id);
      setNotes(data.notes || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.note.trim()) {
      toast.error('Note cannot be empty');
      return;
    }
    try {
      await AdminService.addUserNote(user.id, newNote.note, newNote.type);
      toast.success('Note added successfully');
      setNewNote({ note: '', type: 'general' });
      loadNotes();
    } catch (error) {
      toast.error(error.message || 'Failed to add note');
    }
  };

  const loadAdminTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const data = await AdminService.getAdminTransactions(user.id);
      setAdminTransactions(data.transactions || []);
    } catch (error) {
      console.error('Failed to load admin transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleAddMoney = async () => {
    const amount = parseFloat(addMoneyAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error('Please enter a valid amount (can be positive or negative)');
      return;
    }
    try {
      await AdminService.addMoneyToAccount(user.id, amount, addMoneyReason);
      const isRemoval = amount < 0;
      const absAmount = Math.abs(amount);
      const action = isRemoval ? 'removed' : 'added';
      toast.success(`Successfully ${action} $${absAmount} ${isRemoval ? 'from' : 'to'} account`);
      setAddMoneyAmount('');
      setAddMoneyReason('');
      loadAdminTransactions(); // Refresh admin transactions
      onAction('refresh', user.id); // Refresh user data
    } catch (error) {
      toast.error(error.message || 'Failed to modify account balance');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }
    try {
      await AdminService.deleteNote(noteId);
      toast.success('Note deleted successfully');
      loadNotes();
    } catch (error) {
      toast.error(error.message || 'Failed to delete note');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="modal-content"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>User Details</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="user-details">
            <div className="detail-row">
              <strong>Name:</strong> {user.name || 'N/A'}
            </div>
            <div className="detail-row">
              <strong>Email:</strong> {user.email}
            </div>
            <div className="detail-row">
              <strong>ID:</strong> {user.id}
            </div>
            <div className="detail-row">
              <strong>Status:</strong>
              <div className="status-badges">
                {user.adminLevel === 'master_admin' && <span className="badge master-admin">Master Admin</span>}
                {user.adminLevel === '3' && <span className="badge admin level-3">Level 3</span>}
                {user.adminLevel === '2' && <span className="badge admin level-2">Level 2</span>}
                {user.adminLevel === '1' && <span className="badge admin level-1">Level 1</span>}
                {user.isAdmin && !user.adminLevel && <span className="badge admin">Admin</span>}
                {user.isBanned && <span className="badge banned">Banned</span>}
                {!user.isAdmin && !user.adminLevel && !user.isBanned && (
                  <span className="badge active">Active</span>
                )}
              </div>
            </div>
            {user.progress && (
              <>
                <div className="detail-row">
                  <strong>Level:</strong> {user.progress.level || 1}
                </div>
                <div className="detail-row">
                  <strong>XP:</strong> {user.progress.xp || 0}
                </div>
                <div className="detail-row">
                  <strong>Streak:</strong> {user.progress.streak || 0} days
                </div>
              </>
            )}
            {user.stats && (
              <>
                <div className="detail-row">
                  <strong>Lessons Completed:</strong> {user.stats.lessonsCompleted}
                </div>
                <div className="detail-row">
                  <strong>Quizzes Attempted:</strong> {user.stats.quizzesAttempted}
                </div>
                <div className="detail-row">
                  <strong>Facts Completed:</strong> {user.stats.factsCompleted}
                </div>
                <div className="detail-row">
                  <strong>Transactions:</strong> {user.stats.transactions}
                </div>
                <div className="detail-row">
                  <strong>Chat Messages:</strong> {user.stats.chatMessages}
                </div>
              </>
            )}
            <div className="detail-row">
              <strong>Joined:</strong> {new Date(user.createdAt).toLocaleString()}
            </div>
          </div>

          {/* Admin Notes Section (Level 1+) */}
          {hasAdminLevel('1') && (
            <div className="user-notes-section">
              <h3>Admin Notes</h3>
              <div className="add-note-form">
                <textarea
                  placeholder="Add a note about this user..."
                  value={newNote.note}
                  onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                  rows="3"
                />
                <select
                  value={newNote.type}
                  onChange={(e) => setNewNote({ ...newNote, type: e.target.value })}
                >
                  <option value="general">General</option>
                  <option value="bot_flag">Bot Flag</option>
                  <option value="suspicious">Suspicious</option>
                  <option value="recommendation">Recommendation</option>
                </select>
                <button onClick={handleAddNote}>Add Note</button>
              </div>
              <div className="notes-list">
                {loadingNotes ? (
                  <div>Loading notes...</div>
                ) : notes.length === 0 ? (
                  <div>No notes yet</div>
                ) : (
                  notes.map(note => (
                    <div key={note.id} className={`note-item ${note.isResolved ? 'resolved' : ''}`}>
                      <div className="note-header">
                        <div className="note-header-left">
                          <strong>{note.admin_name || note.admin_email}</strong>
                          <span className={`note-type ${note.type}`}>{note.type}</span>
                          {note.isResolved && <span className="resolved-badge">Resolved</span>}
                        </div>
                        {hasAdminLevel('2') && (
                          <button
                            className="btn-delete-note"
                            onClick={() => handleDeleteNote(note.id)}
                            title="Delete Note"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      <div className="note-body">{note.note}</div>
                      <div className="note-footer">
                        {new Date(note.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Account Management Section (Level 3+) */}
          {hasAdminLevel('3') && (
            <div className="account-management-section">
              <h3>Account Management</h3>
              <div className="add-money-form">
                <input
                  type="number"
                  placeholder="Amount (positive to add, negative to remove)"
                  value={addMoneyAmount}
                  onChange={(e) => setAddMoneyAmount(e.target.value)}
                  step="0.01"
                />
                <input
                  type="text"
                  placeholder="Reason (optional)"
                  value={addMoneyReason}
                  onChange={(e) => setAddMoneyReason(e.target.value)}
                />
                <button onClick={handleAddMoney}>Modify Balance</button>
              </div>

              {/* Admin Transactions History */}
              <div className="admin-transactions-section">
                <h4>Admin Transactions History</h4>
                {loadingTransactions ? (
                  <div>Loading transactions...</div>
                ) : adminTransactions.length === 0 ? (
                  <div>No admin transactions yet</div>
                ) : (
                  <div className="transactions-list">
                    {adminTransactions.map(transaction => {
                      const amount = parseFloat(transaction.total);
                      const isDeposit = amount >= 0;
                      const absAmount = Math.abs(amount);
                      const transactionType = transaction.type === 'admin_withdrawal' ? 'Withdrawal' : 'Deposit';
                      
                      return (
                        <div key={transaction.id} className={`transaction-item admin-transaction ${isDeposit ? 'deposit' : 'withdrawal'}`}>
                          <div className="transaction-header">
                            <div className="transaction-type-info">
                              <strong className="transaction-type-label">Admin {transactionType}</strong>
                              <div className="transaction-admin-info">
                                <span className="admin-label">By:</span>
                                <span className="admin-name">{transaction.admin_name || transaction.admin_email || 'Unknown Admin'}</span>
                              </div>
                            </div>
                            <span className={`transaction-amount ${isDeposit ? 'positive' : 'negative'}`}>
                              {isDeposit ? '+' : '-'}${absAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="transaction-details">
                            {transaction.adminReason && (
                              <div className="transaction-reason">
                                <strong>Reason:</strong> {transaction.adminReason}
                              </div>
                            )}
                            <div className="transaction-date">
                              {new Date(transaction.createdAt).toLocaleString()}
                            </div>
                          </div>
                          <div className={`admin-transaction-badge ${isDeposit ? 'deposit-badge' : 'withdrawal-badge'}`}>
                            Admin {transactionType}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="modal-actions">
            {(() => {
              const targetIsAdmin = user.isAdmin || user.adminLevel === '1' || user.adminLevel === '2' || user.adminLevel === '3' || user.adminLevel === 'master_admin';
              const targetIsMasterAdmin = user.adminLevel === 'master_admin';
              const canActOnThisUser = canActOnUser(user);
              
              return (
                <>
                  {/* Ban/Unban - Level 2+ can ban, but only if they can act on the user */}
                  {hasAdminLevel('2') && canActOnThisUser && (
                    <>
                      {user.isBanned ? (
                        <button
                          className="btn-action btn-unban"
                          onClick={() => onAction('unban', user.id)}
                          disabled={actionLoading[user.id]}
                        >
                          <FaUserCheck /> Unban User
                        </button>
                      ) : (
                        <>
                          <div className="ban-reason-input">
                            <input
                              type="text"
                              placeholder="Ban reason (optional)"
                              value={banReason}
                              onChange={(e) => setBanReason(e.target.value)}
                            />
                          </div>
                          <button
                            className="btn-action btn-ban"
                            onClick={() => onAction('ban', user.id, { reason: banReason })}
                            disabled={actionLoading[user.id] || targetIsMasterAdmin}
                          >
                            <FaBan /> Ban User
                          </button>
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Membership tier - Level 3+ */}
                  {hasAdminLevel('3') && (
                    <div className="tier-controls">
                      <span className="tier-controls-label">
                        Membership: <strong>{user.membershipTier === 'pro' ? 'Pro' : user.membershipTier === 'free' ? 'Free' : 'Pre-Alpha'}</strong>
                      </span>
                      {['pre_alpha', 'free', 'pro'].map((tier) => (
                        <button
                          key={tier}
                          className={`btn-action btn-tier ${user.membershipTier === tier || (!user.membershipTier && tier === 'pre_alpha') ? 'btn-tier-active' : ''}`}
                          onClick={() => onAction('setTier', user.id, { tier })}
                          disabled={actionLoading[user.id] || user.membershipTier === tier}
                        >
                          {tier === 'pre_alpha' ? 'Pre-Alpha' : tier === 'free' ? 'Free' : 'Pro'}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Delete - Level 3+ can delete, but only if they can act on the user */}
                  {/* Top admin (depedroshawn@gmail.com) can delete master admins */}
                  {hasAdminLevel('3') && canActOnThisUser && (!targetIsMasterAdmin || isTopAdmin) && (
                    <button
                      className="btn-action btn-delete"
                      onClick={() => onAction('delete', user.id)}
                      disabled={actionLoading[user.id]}
                    >
                      <FaTrash /> Delete User
                    </button>
                  )}
                  
                  {/* Make/Edit/Remove Admin - Only master admin can manage admins */}
                  {currentUserIsMasterAdmin && (
                    !targetIsAdmin ? (
                      <button
                        className="btn-action btn-admin"
                        onClick={() => onAction('makeAdmin', user.id)}
                        disabled={actionLoading[user.id]}
                      >
                        <FaCrown /> Make Admin
                      </button>
                    ) : !targetIsMasterAdmin ? (
                      <>
                        <button
                          className="btn-action btn-edit-admin"
                          onClick={() => onAction('editAdminLevel', user.id, { currentLevel: user.adminLevel })}
                          disabled={actionLoading[user.id]}
                        >
                          <FaUserShield /> Edit Admin Level
                        </button>
                        <button
                          className="btn-action btn-remove-admin"
                          onClick={() => onAction('removeAdmin', user.id)}
                          disabled={actionLoading[user.id]}
                        >
                          <FaUserTimes /> Remove Admin
                        </button>
                      </>
                    ) : null
                  )}
                  
                  {/* Make/Remove Teacher - Admins can manage teachers */}
                  {hasAdminLevel('1') && (
                    !user.isTeacher ? (
                      <button
                        className="btn-action btn-teacher"
                        onClick={() => onAction('makeTeacher', user.id)}
                        disabled={actionLoading[user.id]}
                      >
                        <FaGraduationCap /> Make Teacher
                      </button>
                    ) : (
                      <button
                        className="btn-action btn-remove-teacher"
                        onClick={() => onAction('removeTeacher', user.id)}
                        disabled={actionLoading[user.id]}
                      >
                        <FaUserTimes /> Remove Teacher
                      </button>
                    )
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Admin Communication View Component
const AdminCommunicationView = ({ adminLevel }) => {
  const [messages, setMessages] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageType, setMessageType] = useState('all'); // 'all', 'sent', 'received'
  const [viewAllCommunications, setViewAllCommunications] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [sendToType, setSendToType] = useState('all'); // 'all', 'specific_admin', 'specific_user', 'admin_levels'
  const [composeData, setComposeData] = useState({
    receiverId: '',
    targetLevels: [],
    subject: '',
    message: '',
    priority: 'normal'
  });
  const isMasterAdmin = adminLevel === 'master_admin';

  useEffect(() => {
    loadMessages();
    loadAdmins();
    loadUsers();
  }, [messageType, viewAllCommunications]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getMessages(messageType, viewAllCommunications);
      setMessages(data.messages || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const data = await AdminService.getAdmins();
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Failed to load admins:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await AdminService.getUsers(1, 100, ''); // Load first 100 users for dropdown
      setUsers(data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!composeData.message.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    
    // Determine receiverId and targetLevels based on sendToType
    let receiverId = null;
    let targetLevels = null;
    
    if (sendToType === 'specific_admin' || sendToType === 'specific_user') {
      if (!composeData.receiverId) {
        toast.error('Please select a recipient');
        return;
      }
      receiverId = composeData.receiverId;
    } else if (sendToType === 'admin_levels') {
      if (composeData.targetLevels.length === 0) {
        toast.error('Please select at least one admin level');
        return;
      }
      targetLevels = composeData.targetLevels;
    }
    // If sendToType === 'all', both receiverId and targetLevels remain null (broadcast to all admins)
    
    try {
      await AdminService.sendMessage(
        receiverId,
        targetLevels,
        composeData.subject,
        composeData.message,
        composeData.priority
      );
      toast.success('Message sent successfully');
      setShowCompose(false);
      setSendToType('all');
      setComposeData({ receiverId: '', targetLevels: [], subject: '', message: '', priority: 'normal' });
      loadMessages();
    } catch (error) {
      toast.error(error.message || 'Failed to send message');
    }
  };

  const handleLevelToggle = (level) => {
    setComposeData(prev => {
      const newLevels = prev.targetLevels.includes(level)
        ? prev.targetLevels.filter(l => l !== level)
        : [...prev.targetLevels, level];
      return { ...prev, targetLevels: newLevels };
    });
  };

  const handleSendToTypeChange = (type) => {
    setSendToType(type);
    setComposeData({ ...composeData, receiverId: '', targetLevels: [] });
  };

  const handleMarkRead = async (messageId) => {
    try {
      await AdminService.markMessageRead(messageId);
      loadMessages();
    } catch (error) {
      toast.error(error.message || 'Failed to mark message as read');
    }
  };

  return (
    <div className="admin-communication-view">
      <div className="communication-header">
        <div className="message-filters">
          <button
            className={messageType === 'all' ? 'active' : ''}
            onClick={() => setMessageType('all')}
          >
            All Messages
          </button>
          <button
            className={messageType === 'received' ? 'active' : ''}
            onClick={() => setMessageType('received')}
          >
            Received
          </button>
          <button
            className={messageType === 'sent' ? 'active' : ''}
            onClick={() => setMessageType('sent')}
          >
            Sent
          </button>
          {isMasterAdmin && (
            <button
              className={viewAllCommunications ? 'active' : ''}
              onClick={() => setViewAllCommunications(!viewAllCommunications)}
              title="View All Communications"
            >
              <FaEye /> View All Communications
            </button>
          )}
        </div>
        <button className="btn-compose" onClick={() => setShowCompose(!showCompose)}>
          <FaComments /> Compose Message
        </button>
      </div>

      {showCompose && (
        <div className="compose-message">
          <h3>Compose Message</h3>
          
          <div className="send-to-section">
            <label>Send To:</label>
            <div className="send-to-options">
              <div className="send-to-type">
                <label>
                  <input
                    type="radio"
                    name="sendType"
                    checked={sendToType === 'all'}
                    onChange={() => handleSendToTypeChange('all')}
                  />
                  All Admins
                </label>
              </div>
              
              <div className="send-to-type">
                <label>
                  <input
                    type="radio"
                    name="sendType"
                    checked={sendToType === 'specific_admin'}
                    onChange={() => handleSendToTypeChange('specific_admin')}
                  />
                  Specific Admin
                </label>
                {sendToType === 'specific_admin' && (
                  <select
                    value={composeData.receiverId}
                    onChange={(e) => setComposeData({ ...composeData, receiverId: e.target.value, targetLevels: [] })}
                  >
                    <option value="">Select Admin...</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.name || admin.email} ({admin.adminLevel || 'Admin'})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              <div className="send-to-type">
                <label>
                  <input
                    type="radio"
                    name="sendType"
                    checked={sendToType === 'specific_user'}
                    onChange={() => handleSendToTypeChange('specific_user')}
                  />
                  Specific User
                </label>
                {sendToType === 'specific_user' && (
                  <select
                    value={composeData.receiverId}
                    onChange={(e) => setComposeData({ ...composeData, receiverId: e.target.value, targetLevels: [] })}
                  >
                    <option value="">Select User...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email} {user.isAdmin ? '(Admin)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              <div className="send-to-type">
                <label>
                  <input
                    type="radio"
                    name="sendType"
                    checked={sendToType === 'admin_levels'}
                    onChange={() => handleSendToTypeChange('admin_levels')}
                  />
                  Specific Admin Levels
                </label>
                {sendToType === 'admin_levels' && (
                  <div className="level-checkboxes">
                    <label>
                      <input
                        type="checkbox"
                        checked={composeData.targetLevels.includes('1')}
                        onChange={() => handleLevelToggle('1')}
                      />
                      Level 1
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={composeData.targetLevels.includes('2')}
                        onChange={() => handleLevelToggle('2')}
                      />
                      Level 2
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={composeData.targetLevels.includes('3')}
                        onChange={() => handleLevelToggle('3')}
                      />
                      Level 3
                    </label>
                    {isMasterAdmin && (
                      <label>
                        <input
                          type="checkbox"
                          checked={composeData.targetLevels.includes('master_admin')}
                          onChange={() => handleLevelToggle('master_admin')}
                        />
                        Master Admin
                      </label>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <input
            type="text"
            placeholder="Subject (optional)"
            value={composeData.subject}
            onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
          />
          <textarea
            placeholder="Message"
            value={composeData.message}
            onChange={(e) => setComposeData({ ...composeData, message: e.target.value })}
            rows="5"
          />
          <select
            value={composeData.priority}
            onChange={(e) => setComposeData({ ...composeData, priority: e.target.value })}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <div className="compose-actions">
            <button onClick={handleSendMessage}>Send</button>
            <button onClick={() => {
              setShowCompose(false);
              setSendToType('all');
              setComposeData({ receiverId: '', targetLevels: [], subject: '', message: '', priority: 'normal' });
            }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="messages-list">
        {loading ? (
          <div className="loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div>No messages found</div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`message-item ${!msg.isRead ? 'unread' : ''}`}>
              <div className="message-header">
                <strong>From: {msg.sender_name || msg.sender_email}</strong>
                {msg.receiver_name ? (
                  <span>To: {msg.receiver_name || msg.receiver_email}</span>
                ) : msg.targetLevels ? (
                  <span className="broadcast level-broadcast">
                    To Levels: {msg.targetLevels.split(',').map(l => l === 'master_admin' ? 'Master Admin' : `Level ${l}`).join(', ')}
                  </span>
                ) : (
                  <span className="broadcast">Broadcast to All</span>
                )}
                <span className={`priority ${msg.priority}`}>{msg.priority}</span>
              </div>
              {msg.subject && <div className="message-subject">{msg.subject}</div>}
              <div className="message-body">{msg.message}</div>
              <div className="message-footer">
                <span>{new Date(msg.createdAt).toLocaleString()}</span>
                {!msg.isRead && msg.receiverId && (
                  <button onClick={() => handleMarkRead(msg.id)}>Mark as Read</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Admin Level Selection Modal
const AdminLevelModal = ({ userId, action, currentLevel, selectedLevel, onLevelChange, onConfirm, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="modal-content admin-level-modal"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{action === 'make' ? 'Make User Admin' : 'Edit Admin Level'}</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="admin-level-selection">
            <label>Select Admin Level:</label>
            <select
              value={selectedLevel}
              onChange={(e) => onLevelChange(e.target.value)}
              className="admin-level-select"
            >
              <option value="1">Level 1 - Can flag users, add notes, send messages</option>
              <option value="2">Level 2 - Can ban users (includes Level 1 permissions)</option>
              <option value="3">Level 3 - Can delete users and manage accounts (includes Level 2 permissions)</option>
              <option value="master_admin">Master Admin - Full control, can manage all admins</option>
            </select>

            <div className="level-description">
              {selectedLevel === '1' && (
                <p className="level-info level-1">
                  <strong>Level 1 Permissions:</strong> View analytics, flag users, add notes about users, send messages to other admins
                </p>
              )}
              {selectedLevel === '2' && (
                <p className="level-info level-2">
                  <strong>Level 2 Permissions:</strong> All Level 1 permissions + Can ban/unban users (except other admins)
                </p>
              )}
              {selectedLevel === '3' && (
                <p className="level-info level-3">
                  <strong>Level 3 Permissions:</strong> All Level 2 permissions + Can delete users, add money to accounts
                </p>
              )}
              {selectedLevel === 'master_admin' && (
                <p className="level-info master-admin">
                  <strong>Master Admin Permissions:</strong> All Level 3 permissions + Can manage other admins (promote, demote, remove)
                </p>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn-action btn-confirm" onClick={onConfirm}>
              <FaCheck /> Confirm
            </button>
            <button className="btn-action btn-cancel" onClick={onClose}>
              <FaTimes /> Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Leagues View Component
const LeaguesView = ({
  leagues,
  teachers,
  selectedLeague,
  setSelectedLeague,
  showCreateLeague,
  setShowCreateLeague,
  newLeague,
  setNewLeague,
  showAssignTeacher,
  setShowAssignTeacher,
  onRefresh,
  canDeleteClassrooms
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await LeagueService.createLeague(newLeague);
      toast.success('League created successfully!');
      setNewLeague({ name: '', description: '' });
      setShowCreateLeague(false);
      onRefresh();
    } catch (error) {
      toast.error(error.error || 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTeacher = async (e) => {
    e.preventDefault();
    if (!selectedLeague || !selectedTeacherId) return;
    try {
      setLoading(true);
      await LeagueService.assignTeacher(selectedLeague.id, selectedTeacherId);
      toast.success('Teacher assigned successfully!');
      setShowAssignTeacher(false);
      setSelectedTeacherId('');
      onRefresh();
    } catch (error) {
      toast.error(error.error || 'Failed to assign teacher');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLeague = async (leagueId) => {
    try {
      const data = await LeagueService.getLeague(leagueId);
      setSelectedLeague(data.league);
    } catch (error) {
      toast.error(error.error || 'Failed to load league details');
    }
  };

  const handleMakeTeacher = async (userId) => {
    try {
      await LeagueService.makeTeacher(userId);
      toast.success('User promoted to teacher successfully!');
      onRefresh();
    } catch (error) {
      toast.error(error.error || 'Failed to promote user to teacher');
    }
  };

  const handleDeleteLeague = async (league) => {
    if (!canDeleteClassrooms || !league?.id) return;
    const label = league.name || 'this class';
    if (
      !window.confirm(
        `Delete "${label}"? Students will be removed from this class (accounts stay). Teacher curriculum and assignments for this class will be removed. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      await LeagueService.deleteLeague(league.id);
      toast.success('League deleted');
      if (selectedLeague?.id === league.id) {
        setSelectedLeague(null);
      }
      onRefresh();
    } catch (error) {
      toast.error(error.error || error.details || 'Failed to delete league');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="leagues-admin-view">
      <div className="leagues-header">
        <h2>League Management</h2>
        <button
          className="btn-create"
          onClick={() => setShowCreateLeague(true)}
        >
          <FaPlus /> Create League
        </button>
      </div>

      {showCreateLeague && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowCreateLeague(false)}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Create New League</h3>
            <form onSubmit={handleCreateLeague}>
              <div className="form-group">
                <label>League Name</label>
                <input
                  type="text"
                  value={newLeague.name}
                  onChange={(e) => setNewLeague({ ...newLeague, name: e.target.value })}
                  required
                  placeholder="e.g., Mrs. Smith's 5th Grade Class"
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={newLeague.description}
                  onChange={(e) => setNewLeague({ ...newLeague, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-action btn-confirm" disabled={loading}>
                  <FaCheck /> Create
                </button>
                <button
                  type="button"
                  className="btn-action btn-cancel"
                  onClick={() => {
                    setShowCreateLeague(false);
                    setNewLeague({ name: '', description: '' });
                  }}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      <div className="leagues-list-admin">
        {leagues.length === 0 ? (
          <div className="empty-state">
            <FaUsers size={48} />
            <p>No leagues found. Create a league to get started.</p>
          </div>
        ) : (
          <div className="leagues-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Students</th>
                  <th>Teachers</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leagues.map((league) => (
                  <tr key={league.id}>
                    <td>{league.name}</td>
                    <td><code>{league.code}</code></td>
                    <td>{league.student_count || 0}</td>
                    <td>{league.teacher_count || 0}</td>
                    <td>{new Date(league.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-action btn-view"
                        onClick={() => handleViewLeague(league.id)}
                      >
                        <FaEye /> View
                      </button>
                      <button
                        className="btn-action btn-assign"
                        onClick={() => {
                          setSelectedLeague(league);
                          setShowAssignTeacher(true);
                        }}
                      >
                        <FaUserPlus /> Assign Teacher
                      </button>
                      {canDeleteClassrooms && (
                        <button
                          type="button"
                          className="btn-action btn-danger"
                          disabled={loading}
                          onClick={() => handleDeleteLeague(league)}
                        >
                          <FaTrash /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedLeague && !showAssignTeacher && (
        <div className="league-details-modal">
          <div className="modal-header">
            <h3>{selectedLeague.name}</h3>
            <button onClick={() => setSelectedLeague(null)}>
              <FaTimes />
            </button>
          </div>
          <div className="league-details-content">
            <p><strong>Code:</strong> <code>{selectedLeague.code}</code></p>
            <p><strong>Students:</strong> {selectedLeague.students?.length || 0}</p>
            <p><strong>Teachers:</strong> {selectedLeague.teachers?.length || 0}</p>
            {selectedLeague.teachers && selectedLeague.teachers.length > 0 && (
              <div>
                <h4>Teachers:</h4>
                <ul>
                  {selectedLeague.teachers.map((teacher) => (
                    <li key={teacher.id}>
                      {teacher.name || teacher.email}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {canDeleteClassrooms && (
              <div className="modal-actions" style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="btn-action btn-danger"
                  disabled={loading}
                  onClick={() => handleDeleteLeague(selectedLeague)}
                >
                  <FaTrash /> Delete league
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showAssignTeacher && selectedLeague && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => {
            setShowAssignTeacher(false);
            setSelectedTeacherId('');
          }}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Assign Teacher to {selectedLeague.name}</h3>
            <form onSubmit={handleAssignTeacher}>
              <div className="form-group">
                <label>Select Teacher</label>
                <select
                  value={selectedTeacherId}
                  onChange={(e) => setSelectedTeacherId(e.target.value)}
                  required
                >
                  <option value="">Select a teacher...</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name || teacher.email} {teacher.league_count > 0 && `(${teacher.league_count} leagues)`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-action btn-confirm" disabled={loading}>
                  <FaCheck /> Assign
                </button>
                <button
                  type="button"
                  className="btn-action btn-cancel"
                  onClick={() => {
                    setShowAssignTeacher(false);
                    setSelectedTeacherId('');
                  }}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      <div className="teachers-section">
        <h3>Teachers</h3>
        <div className="teachers-list">
          {teachers.length === 0 ? (
            <p>No teachers found. Promote users to teachers in the Users tab.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Leagues</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td>{teacher.name || 'N/A'}</td>
                    <td>{teacher.email}</td>
                    <td>{teacher.league_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;


